/**
 * Which of the two views of the archive this person is in.
 *
 * The Worker decides — `caps.browse`, from the role (see migration 0012) — and refuses
 * anything outside a view-only account's few routes on its own. Nothing here is a
 * security measure; it is what keeps the app honest about the decision the server has
 * already made, so a viewer is not shown a sidebar full of pages that answer 403, or a
 * partner's name underlined as a link to a record they cannot open.
 *
 * A context of its own rather than a prop threaded through every page, and its own
 * module rather than App's, because the shared presentational components need it too —
 * and App imports those, so they cannot import App.
 */
import { createContext, useContext, useMemo } from 'react'

/* Full access is the default. A component rendered outside the provider — a test, a
   page mounted on its own — behaves the way the archive always has. */
const Ctx = createContext({ canBrowse: true, anonVersion: 0 })

export const ViewModeProvider = ({ canBrowse, anonVersion, children }) => {
  // Memoised on its two values: this sits above every page, and a fresh object each
  // render would re-render all of them for nothing.
  const value = useMemo(() => ({ canBrowse, anonVersion }), [canBrowse, anonVersion])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/**
 * True for an account with the run of the archive; false for the view-only one, and for
 * every donor, since a donor is a viewer arriving at the other hostname.
 *
 * It is worth knowing how much rests on this one boolean, because it is more than the
 * sidebar it was written for. It chooses which edition of an annual report the Worker
 * serves, and — following from that — whether a report opens in the browser's PDF viewer
 * or as pictures of its pages with no file behind them. Anything that should be true of
 * "a reader who is shown the archive with the faces covered" belongs on this and not on a
 * role name or a hostname, which are two ways of arriving at the same answer and two
 * things to keep in step.
 */
export const useCanBrowse = () => useContext(Ctx).canBrowse

/**
 * Which generation of the anonymising to ask for. It goes in the image URL rather than
 * being left to the server, because the browser caches these for an hour against the
 * URL alone — so without it a new recipe reaches only the pictures nobody had opened
 * yet, and a page shows two generations at once. See ANON in the Worker.
 */
export const useAnonVersion = () => useContext(Ctx).anonVersion

/**
 * The pages a view-only account is given, plus the record of a single update, which is
 * what three of them open. Everything else is not part of that view.
 *
 * Kept beside the hook rather than in App because both the router and the sidebar ask
 * the same question of it, and the answer has to be the same in both places.
 *
 * `/reports/newsletters-by-partner` — the Updates by Partner Unit report — was here and
 * is not any more. It was the only report of the seven that a view-only account or a
 * donor was given, and the endpoint behind it is now refused to both (viewerMayReach in
 * the Worker), so leaving the path would have meant a sidebar link to a page of errors.
 */
export const VIEWER_PATHS = [
  '/',
  '/annual-reports',
  '/prayer-requests',
  '/highlighted-needs',
  '/settings/appearance',
]
